/**
 * GuidedManualStep — renders a step-by-step guide loaded from the
 * `tracking_guides/{guideId}` Firestore doc.
 *
 * Validation is fired on blur, on paste-complete, or after the user stops
 * typing for 800 ms — never on every keystroke (avoids test-event bursts).
 * The raw token is sent in the request body with testEventCode='TEST12345'
 * so guide-flow checks land in Meta's Test Events, never as real conversions.
 *
 * Token is saved via trackingApi.setFormSecret (secret store only).
 * The form doc receives hasAccessToken:true — never the plaintext token.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { trackingApi } from '../utils/api';

const DEBOUNCE_MS = 800;
const GUIDED_TEST_EVENT_CODE = 'TEST12345';

/**
 * Static guide registry — loaded synchronously; avoids a Firestore round-trip
 * for new platforms while leaving `meta_capi_token` (Firestore-seeded) untouched.
 * Step copy intentionally mirrors HELP_CONTENT in TrackingConfig.jsx so there
 * is one source of truth for platform menu paths.
 */
const LOCAL_GUIDES = {
  google_ads_conversion: {
    id: 'google_ads_conversion',
    platform: 'google_ads',
    title: 'Find your Google Ads Conversion ID and Label',
    verifiedAsOf: '2026-06-15',
    steps: [
      {
        order: 1,
        text: 'Open Google Ads. Click the Tools icon (wrench) → Measurement → Conversions.',
        deepLinkUrl: 'https://ads.google.com',
        deepLinkLabel: 'Open Google Ads →',
        annotationImageUrl: '',
      },
      {
        order: 2,
        text: 'Click the conversion action you want to track (or create one for this form).',
        deepLinkUrl: '',
        deepLinkLabel: '',
        annotationImageUrl: '',
      },
      {
        order: 3,
        text: 'Click "Tag setup". Your Conversion ID (format: AW-123456789) and Conversion Label appear in the snippet. Copy both, then paste them into the fields above.',
        deepLinkUrl: '',
        deepLinkLabel: '',
        annotationImageUrl: '',
      },
    ],
    fallbackText:
      "Google Ads rearranges periodically. Look for 'Tools & Settings', 'Measurement', or 'Conversions'. The Conversion ID always starts with AW- and the Label is the alphanumeric string in the send_to value.",
    reportEmail: 'donnie@goodatmarketing.com',
    inputField: { label: '', validationPlatform: 'google_ads', validationField: '' },
    deepLinksToCheck: ['https://ads.google.com'],
  },

  linkedin_conversion: {
    id: 'linkedin_conversion',
    platform: 'linkedin',
    title: 'Find your LinkedIn Conversion ID and Partner ID',
    verifiedAsOf: '2026-06-15',
    steps: [
      {
        order: 1,
        text: 'Open LinkedIn Campaign Manager.',
        deepLinkUrl: 'https://www.linkedin.com/campaignmanager',
        deepLinkLabel: 'Open Campaign Manager →',
        annotationImageUrl: '',
      },
      {
        order: 2,
        text: 'Click Account Assets → Conversion Tracking (or "Conversions").',
        deepLinkUrl: '',
        deepLinkLabel: '',
        annotationImageUrl: '',
      },
      {
        order: 3,
        text: 'Click a conversion to view its details. The Conversion ID appears in the page URL or the details panel.',
        deepLinkUrl: '',
        deepLinkLabel: '',
        annotationImageUrl: '',
      },
      {
        order: 4,
        text: 'Your Partner ID is the 6-digit account ID in the top-left corner of Campaign Manager (also called Insight Tag ID). Copy both values and paste them into the fields above.',
        deepLinkUrl: '',
        deepLinkLabel: '',
        annotationImageUrl: '',
      },
    ],
    fallbackText:
      "LinkedIn's UI changes periodically. Look for 'Account Assets', 'Conversions', or 'Insight Tag'. The Partner ID is your 6-digit LinkedIn account ID.",
    reportEmail: 'donnie@goodatmarketing.com',
    inputField: { label: '', validationPlatform: 'linkedin', validationField: '' },
    deepLinksToCheck: ['https://www.linkedin.com/campaignmanager'],
  },

  tiktok_pixel: {
    id: 'tiktok_pixel',
    platform: 'tiktok',
    title: 'Find your TikTok Pixel ID',
    verifiedAsOf: '2026-06-15',
    steps: [
      {
        order: 1,
        text: 'Open TikTok Ads Manager → Events → Web Events (or "Events Manager").',
        deepLinkUrl: 'https://ads.tiktok.com',
        deepLinkLabel: 'Open TikTok Ads Manager →',
        annotationImageUrl: '',
      },
      {
        order: 2,
        text: 'Select or create a pixel. The Pixel ID (exactly 19 characters) is shown next to the pixel name.',
        deepLinkUrl: '',
        deepLinkLabel: '',
        annotationImageUrl: '',
      },
      {
        order: 3,
        text: 'Copy the Pixel ID and paste it into the field above.',
        deepLinkUrl: '',
        deepLinkLabel: '',
        annotationImageUrl: '',
      },
    ],
    fallbackText:
      "TikTok's Ads Manager navigation changes. Look for 'Events', 'Web Events', or 'Pixels'. The Pixel ID is always 19 characters.",
    reportEmail: 'donnie@goodatmarketing.com',
    inputField: { label: '', validationPlatform: 'tiktok', validationField: '' },
    deepLinksToCheck: ['https://ads.tiktok.com'],
  },

  tiktok_capi_token: {
    id: 'tiktok_capi_token',
    platform: 'tiktok',
    title: 'Generate your TikTok Events API Access Token',
    verifiedAsOf: '2026-06-15',
    steps: [
      {
        order: 1,
        text: 'Open TikTok Ads Manager → Events → Web Events.',
        deepLinkUrl: 'https://ads.tiktok.com',
        deepLinkLabel: 'Open TikTok Ads Manager →',
        annotationImageUrl: '',
      },
      {
        order: 2,
        text: 'Select your pixel, then click Settings → Events API.',
        deepLinkUrl: '',
        deepLinkLabel: '',
        annotationImageUrl: '',
      },
      {
        order: 3,
        text: 'Click "Generate Access Token" and copy the token.',
        deepLinkUrl: '',
        deepLinkLabel: '',
        annotationImageUrl: '',
      },
      {
        order: 4,
        text: 'Paste the token in the field below and click "Save token".',
        deepLinkUrl: '',
        deepLinkLabel: '',
        annotationImageUrl: '',
      },
    ],
    fallbackText:
      "TikTok's UI rearranges periodically. Look for 'Events API', 'Settings', or 'Web Events'. The Events API token is under your pixel's Settings tab.",
    reportEmail: 'donnie@goodatmarketing.com',
    inputField: {
      label: 'Paste your TikTok Events API token here',
      validationPlatform: 'tiktok',
      validationField: 'accessToken',
    },
    deepLinksToCheck: ['https://ads.tiktok.com'],
  },
};

function statusStyle(status) {
  switch (status) {
    case 'verified':
    case 'offline_armed':
      return { color: '#1b5e20', background: '#e8f5e9', border: '1px solid #a5d6a7' };
    case 'error':
      return { color: '#b71c1c', background: '#ffebee', border: '1px solid #ef9a9a' };
    case 'testing':
      return { color: '#555', background: '#f5f5f5', border: '1px solid #e0e0e0' };
    default:
      return {};
  }
}

export default function GuidedManualStep({ guideId, pixelConfig, formId, onComplete }) {
  const [guide, setGuide] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);

  const [token, setToken] = useState('');
  const [validStatus, setValidStatus] = useState(null); // null | 'testing' | 'verified' | 'offline_armed' | 'error'
  const [validMsg, setValidMsg] = useState('');
  const [nextTip, setNextTip] = useState('');

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [showFallback, setShowFallback] = useState(false);

  const debounceRef = useRef(null);

  useEffect(() => {
    // Local registry first — no network call. Firestore fallback for meta_capi_token
    // and any future seeded guides that aren't in the local registry yet.
    if (LOCAL_GUIDES[guideId]) {
      setGuide(LOCAL_GUIDES[guideId]);
      setLoading(false);
      return;
    }
    getDoc(doc(db, 'tracking_guides', guideId))
      .then((snap) => {
        if (snap.exists()) {
          setGuide(snap.data());
        } else {
          setLoadError(true);
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [guideId]);

  const runValidation = useCallback(
    async (value) => {
      const trimmed = value.trim();
      if (!trimmed) {
        setValidStatus(null);
        setValidMsg('');
        setNextTip('');
        return;
      }

      setValidStatus('testing');
      setValidMsg('Checking token…');
      setNextTip('');

      try {
        const platform = guide?.inputField?.validationPlatform || 'meta';
        const result = await trackingApi.testPixel(
          platform,
          { ...pixelConfig, accessToken: trimmed },
          formId,
          GUIDED_TEST_EVENT_CODE
        );

        const s = result?.status;
        if (s === 'verified' || s === 'offline_armed') {
          setValidStatus(s);
          setValidMsg('✓ Closed-deal reporting armed.');
          setNextTip('');
        } else {
          setValidStatus('error');
          setValidMsg(result?.message || 'Token check failed. Check the token and try again.');
          setNextTip(result?.next ? `→ Next: ${result.next}` : '');
        }
      } catch (err) {
        setValidStatus('error');
        setValidMsg(`✗ Could not verify: ${err.message}`);
        setNextTip('');
      }
    },
    [guide, pixelConfig, formId]
  );

  function handleChange(e) {
    const value = e.target.value;
    setToken(value);
    setValidStatus(null);
    setSaveError('');
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runValidation(value), DEBOUNCE_MS);
  }

  function handleBlur() {
    clearTimeout(debounceRef.current);
    if (token.trim()) runValidation(token);
  }

  function handlePaste(e) {
    // Read the pasted text before React has processed the event
    const pasted = e.clipboardData?.getData('text') || '';
    clearTimeout(debounceRef.current);
    // Use a short delay to let the input value settle, then validate the pasted value
    debounceRef.current = setTimeout(() => runValidation(pasted || token), 150);
  }

  async function handleSave() {
    if (!token.trim() || !['verified', 'offline_armed'].includes(validStatus)) return;
    setSaving(true);
    setSaveError('');
    try {
      const platform = guide?.inputField?.validationPlatform || guide?.platform || 'meta';
    const result = await trackingApi.setFormSecret(formId, platform, token.trim());
      if (!result.success) throw new Error(result.error || 'Token storage failed');
      setSaved(true);
      onComplete?.({ ...pixelConfig, hasAccessToken: true });
    } catch (err) {
      setSaveError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ fontSize: '0.9rem', color: '#666', padding: '0.75rem 0' }}>
        Loading guide…
      </div>
    );
  }

  if (loadError || !guide) {
    return (
      <div style={{ fontSize: '0.9rem', color: '#b71c1c', padding: '0.75rem 0' }}>
        Guide not found.{' '}
        <a
          href={`mailto:donnie@goodatmarketing.com?subject=Guide+not+found:+${guideId}`}
          style={{ color: '#b71c1c' }}
        >
          Report this.
        </a>
      </div>
    );
  }

  const { title, verifiedAsOf, steps = [], fallbackText, reportEmail, inputField } = guide;
  const sortedSteps = [...steps].sort((a, b) => a.order - b.order);
  const isArmed = validStatus === 'verified' || validStatus === 'offline_armed';
  const contactEmail = reportEmail || 'donnie@goodatmarketing.com';

  return (
    <div
      style={{
        border: '1px solid #e3e8ef',
        borderRadius: '8px',
        padding: '1.25rem',
        background: '#f8fafc',
        marginTop: '0.75rem',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '0.875rem',
          gap: '1rem',
        }}
      >
        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', lineHeight: '1.3' }}>
          {title}
        </h4>
        <span
          style={{
            fontSize: '0.72rem',
            color: '#888',
            whiteSpace: 'nowrap',
            marginTop: '0.15rem',
          }}
        >
          Verified as of {verifiedAsOf}
        </span>
      </div>

      {/* Steps */}
      <ol style={{ paddingLeft: '1.5rem', margin: '0 0 1.125rem' }}>
        {sortedSteps.map((step) => (
          <li
            key={step.order}
            style={{ marginBottom: '0.75rem', fontSize: '0.9rem', lineHeight: '1.5' }}
          >
            <span>{step.text}</span>
            {step.deepLinkUrl && (
              <a
                href={step.deepLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  marginLeft: '0.5rem',
                  fontSize: '0.85rem',
                  color: '#1a73e8',
                  textDecoration: 'none',
                  fontWeight: '500',
                }}
              >
                {step.deepLinkLabel || 'Open →'}
              </a>
            )}
            {step.annotationImageUrl && (
              <div style={{ marginTop: '0.5rem' }}>
                <img
                  src={step.annotationImageUrl}
                  alt={`Step ${step.order} screenshot`}
                  style={{
                    maxWidth: '100%',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}
                />
              </div>
            )}
          </li>
        ))}
      </ol>

      {/* Token input — only shown when the guide has an inputField with a label */}
      {inputField?.label && (
        <div style={{ marginBottom: '0.875rem' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '0.4rem',
              fontWeight: '500',
              fontSize: '0.9rem',
            }}
          >
            {inputField.label}
          </label>
          <input
            type="password"
            value={token}
            onChange={handleChange}
            onBlur={handleBlur}
            onPaste={handlePaste}
            placeholder="Paste token here"
            disabled={saved}
            style={{
              width: '100%',
              padding: '0.65rem 0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem',
              boxSizing: 'border-box',
              background: saved ? '#f5f5f5' : '#fff',
            }}
          />

          {/* Validation status */}
          {validStatus && (
            <div
              style={{
                marginTop: '0.4rem',
                padding: '0.4rem 0.65rem',
                borderRadius: '4px',
                fontSize: '0.85rem',
                ...statusStyle(validStatus),
              }}
            >
              {validMsg}
              {nextTip && (
                <div style={{ marginTop: '0.2rem', fontStyle: 'italic' }}>{nextTip}</div>
              )}
            </div>
          )}

          {/* Save button — shown once token passes validation and not yet saved */}
          {isArmed && !saved && (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                marginTop: '0.75rem',
                padding: '0.55rem 1.25rem',
                background: saving ? '#90caf9' : '#1a73e8',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: saving ? 'default' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
              }}
            >
              {saving ? 'Saving…' : 'Save token'}
            </button>
          )}

          {saveError && (
            <div
              style={{
                marginTop: '0.4rem',
                fontSize: '0.85rem',
                color: '#b71c1c',
              }}
            >
              ✗ {saveError}
            </div>
          )}

          {saved && (
            <div
              style={{
                marginTop: '0.5rem',
                color: '#1b5e20',
                fontWeight: '500',
                fontSize: '0.9rem',
              }}
            >
              ✓ Closed-deal reporting armed. Token secured.
            </div>
          )}
        </div>
      )}

      {/* Footer — fallback + report link */}
      <div
        style={{
          borderTop: '1px solid #e3e8ef',
          paddingTop: '0.75rem',
          fontSize: '0.8rem',
          color: '#666',
          display: 'flex',
          gap: '1.25rem',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <button
          onClick={() => setShowFallback((f) => !f)}
          style={{
            background: 'none',
            border: 'none',
            color: '#1a73e8',
            cursor: 'pointer',
            fontSize: '0.8rem',
            padding: 0,
            textDecoration: 'underline',
          }}
        >
          Looks different on your screen?
        </button>
        <a
          href={`mailto:${contactEmail}?subject=Guide+looks+wrong:+${encodeURIComponent(guideId)}`}
          style={{ color: '#666', fontSize: '0.8rem' }}
        >
          Report this guide is wrong
        </a>
      </div>

      {showFallback && fallbackText && (
        <div
          style={{
            marginTop: '0.6rem',
            fontSize: '0.85rem',
            color: '#555',
            background: '#fffde7',
            padding: '0.65rem 0.75rem',
            borderRadius: '4px',
            border: '1px solid #fff176',
            lineHeight: '1.5',
          }}
        >
          {fallbackText}
        </div>
      )}
    </div>
  );
}
